/**
 * Navigation telemetry, sent to GA4 only.
 *
 * WHY GA4 AND NOT OUR OWN PIPELINE. `/api/event` is same-origin and is matched by `proxy.ts`, so
 * every first-party beacon is a billable Vercel Edge Request — and Edge Requests are ~79% of the
 * bill. `gtag` posts to Google, so these cost us nothing, add no rows to `tool_events`, and cannot
 * repeat the 2026-06-19 insert-flood. Navigation is high-frequency by nature, which is exactly the
 * shape that must not go through our own endpoint.
 *
 * WHY THIS EXISTS AT ALL. GA4 cannot answer "is the sidebar used" on its own:
 *   - Enhanced Measurement's `click` event is OUTBOUND-ONLY, so internal nav has never been
 *     recorded by anything;
 *   - `view_search_results` needs the query in the URL, and the sidebar search is pure React state
 *     (`Sidebar.tsx`) that never touches the router — so it is invisible to GA4 *and* to every
 *     third-party tool we evaluated. Only an explicit event can see it.
 *
 * CONSENT. Nothing to add. The GA4 Consent Mode default is `analytics_storage: denied`
 * (`app/layout.tsx`) and `CookieConsent.tsx` flips it on acceptance; gtag holds events back on its
 * own until then. So these inherit the existing banner rather than needing a new category.
 *
 * PRIVACY. Deliberately no free text, no query strings, no user identifiers. The search event
 * carries the LENGTH of the query and the NUMBER of results, never the query itself — a search
 * term is user-authored content and would put it in a third party's logs for no analytical gain.
 */

/** Where a tool open came from. Keep this list closed — an open vocabulary cannot be summed. */
export type NavSurface =
  | 'sidebar'
  | 'sidebar-search'
  | 'sidebar-subsection'
  | 'homepage-grid'
  | 'related-tools'
  | 'saved'
  | 'in-tool';

/**
 * Fire-and-forget. Never throws, never blocks navigation.
 *
 * gtag is absent far more often than you would guess — ad blockers, the script failing to load,
 * consent not yet granted, or a bot. All of those must be silent no-ops: telemetry that can break
 * a navigation is worse than no telemetry.
 */
function send(event: string, params: Record<string, string | number | boolean>): void {
  if (typeof window === 'undefined') return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtag = (window as any).gtag;
    if (typeof gtag === 'function') gtag('event', event, params);
  } catch {
    /* analytics must never surface to the user */
  }
}

/**
 * A tool was opened, and from which control.
 *
 * ⚠️ Call this on the CLICK, not on the destination's mount. The destination cannot tell what was
 * clicked: sidebar links are `next/link`, so there is no document load and `document.referrer`
 * does not change — it still holds whatever brought the visitor to the session's first page.
 */
export function trackNavClick(tool: string, surface: NavSurface): void {
  send('nav_click', { tool, surface });
}

/**
 * The sidebar search was used. Fires on a debounced pause in typing, NOT per keystroke —
 * `onChange` fires per character, and one row per character is the flood shape.
 *
 * `results: 0` is the interesting case: it separates "nobody searches" from "people search and
 * find nothing", which have opposite fixes (remove the box vs fix the matcher).
 */
export function trackSidebarSearch(queryLength: number, results: number): void {
  send('sidebar_search', { query_length: queryLength, results });
}

/**
 * The sidebar was collapsed or expanded.
 *
 * Answers whether the collapse control earns its place. Note the stored preference is only ever
 * written by the toggle, so its ABSENCE proves a user has never touched it — which is why the
 * width state is also reported on `nav_click` below rather than inferred from this event alone.
 */
export function trackSidebarToggle(collapsed: boolean): void {
  send('sidebar_toggle', { collapsed });
}
